'use client';

import ActionButton from '@/components/form/ActionButton/ActionButton';
import FormFile from '@/components/form/FormFile/FormFile';
import FormSelect from '@/components/form/FormSelect/FormSelect';
import FormText from '@/components/form/FormText/FormText';
import FormTextArea from '@/components/form/FormTextArea/FormTextArea';
import { swal } from '@/lib/sweetalert';
import { yup } from '@/lib/yup';
import layoutStyles from '@/styles/modules/layout.module.scss';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { pickErrorMessages } from '@/utils/functions';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { useContactAtom } from '../../state';
import type { ContactCategory, ContactFormValues } from '../../types';

type Props = {
  name?: string;
  email?: string;
  categories: Array<ContactCategory>;
};

const formScheme = yup.object().shape({
  selectCategoryId: yup
    .number()
    .min(1, 'お問い合わせ内容を選択してください　＊必須項目です')
    .required('お問い合わせ内容を選択してください　＊必須項目です'),
  name: yup.string().required(),
  email: yup.string().email().required(),
  description: yup.string().required(),
});

const fileForm = (formNum: number, control: Control<ContactFormValues>) => {
  return [...Array(formNum)].map((_, i) => (
    <FormFile
      // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
      key={i}
      label={i === 0 ? '添付画像（JPG/PNG/WEBP/HEIC/MP4/MOV/QT/HEVC 1GBまで）' : ''}
      name={`file${i}`}
      control={control}
    />
  ));
};

const ContactForm = ({ categories, email, name }: Props) => {
  const router = useRouter();
  const [_contactValue, setContactValue] = useContactAtom();
  const [isLoading, setIsLoading] = useState(false);
  const isLoggedIn = email !== '';
  const attachFileNum = isLoggedIn ? 5 : 1;

  const { handleSubmit, getValues, control } = useForm<ContactFormValues>({
    mode: 'onChange',
    defaultValues: {
      selectCategoryId: 0,
      name: name,
      email: email,
      description: '',
      file0: null,
      file1: null,
      file2: null,
      file3: null,
      file4: null,
    },
    resolver: yupResolver(formScheme),
  });

  const getFileFormVal = (formNum: number) => {
    return [...Array(formNum)].map((_, i) => getValues(`file${i}`)).filter((f): f is File => f != null);
  };

  const getSelectCategory = (selectCategoryId: number) => {
    return categories.find((category) => category.id === selectCategoryId) as ContactCategory;
  };

  const submit = async () => {
    try {
      setIsLoading(true);
      setContactValue({
        selectCategory: getSelectCategory(Number(getValues('selectCategoryId'))),
        name: getValues('name'),
        email: getValues('email'),
        description: getValues('description'),
        files: getFileFormVal(attachFileNum),
      });
      router.push('/contact/confirm');
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
      setIsLoading(false);
    }
  };

  const submitError = () => {
    swal.invalid();
  };

  const emptyOption = [{ value: 0, text: '--選択してください--' }];
  const categoryOption = categories.map((item) => ({
    value: item.id,
    text: item.name,
  }));
  const totalOption = emptyOption.concat(categoryOption);

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <div className={layoutStyles.formWrapper}>
        <FormSelect
          label='問い合わせ内容'
          name='selectCategoryId'
          items={totalOption}
          control={control}
          required
          outlined
        />
        <FormText label='名前' name='name' control={control} required />
        <FormText label='メールアドレス' name='email' control={control} readOnly={isLoggedIn} required />
        <FormTextArea label='問い合わせ内容' name='description' control={control} required outlined={true} />
        {fileForm(attachFileNum, control)}
      </div>
      <div className={`${layoutStyles.centeredBtnWrapper} ${utilsStyles.mtMedium}`}>
        <ActionButton onClick={handleSubmit(submit, submitError)} disabled={isLoading}>
          確認する
        </ActionButton>
      </div>
    </form>
  );
};
export default ContactForm;
